import ActionButton from "#/components/buttons/ActionButton";
import ImageLightbox from "#/components/overlays/ImageLightbox";
import { Button } from "@mantine/core";
import { IconImageInPicture, IconTrash } from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ACCEPTED_TYPES = "image/jpeg, image/png, image/webp";

export interface ImagePickerProps {
  label: string;
  /** Currently stored image URL (from the server). */
  value?: string | null;
  /** Fires with the picked file, or `null` when the user removes the image. */
  onChange: (file: File | null) => void;
  /** Picked file, when the parent owns it (controlled). Pass `null` to clear the pick from outside. */
  file?: File | null;
  disabled?: boolean;
  /** Show a remove button that clears both the picked file and the stored URL. */
  clearable?: boolean;
  alt?: string;
  /** `inline`: small thumbnail with the controls beside it. `box`: large preview filling the container, controls underneath. */
  variant?: "inline" | "box";
}

const ImagePicker = ({
  label,
  value,
  onChange,
  disabled,
  clearable,
  alt,
  variant = "inline",
  file: controlledFile,
}: ImagePickerProps) => {
  const { t } = useTranslation();
  const [internalFile, setInternalFile] = useState<File | null>(null);
  const file = controlledFile !== undefined ? controlledFile : internalFile;

  // Uncontrolled: a new stored URL (after save) or an external clear supersedes any picked file.
  useEffect(() => {
    setInternalFile(null);
  }, [value]);

  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const previewUrl = objectUrl ?? value ?? null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;
    setInternalFile(selected);
    onChange(selected);
  };

  const handleClear = () => {
    setInternalFile(null);
    onChange(null);
  };

  const isBox = variant === "box";
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Box variant: the preview's height comes from the sibling column; mirror it
  // onto the width so the preview is always square. CSS aspect-ratio can't do
  // this inside an auto-sized grid column, so measure it.
  const previewRef = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!isBox || !previewRef.current) return;
    const el = previewRef.current;
    const observer = new ResizeObserver(() => {
      setSide(Math.round(el.getBoundingClientRect().height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isBox]);

  const preview = (
    <div
      ref={previewRef}
      style={isBox && side ? { width: side } : undefined}
      className={clsx(
        "relative border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden",
        isBox ? "flex-1 min-h-0" : "w-24 h-24",
      )}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={alt ?? label}
          role="button"
          tabIndex={0}
          aria-label={t("common.imagePicker.viewFullScreen")}
          title={t("common.imagePicker.viewFullScreen")}
          className={clsx(
            "absolute inset-0 w-full h-full cursor-zoom-in",
            isBox ? "object-contain" : "object-cover",
          )}
          onClick={() => setLightboxOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setLightboxOpen(true);
          }}
        />
      ) : (
        <IconImageInPicture size={40} />
      )}
    </div>
  );

  const controls = (
    <div className="flex flex-col gap-3 w-full">
      <span className={clsx(isBox && "text-xs")}>{t("common.imagePicker.selectImage")}</span>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Button variant="outline" disabled={disabled}>
            {t("common.imagePicker.selectFile")}
          </Button>
          <input
            type="file"
            accept={ACCEPTED_TYPES}
            disabled={disabled}
            aria-label={label}
            className="w-full h-full absolute top-0 left-0 z-10 opacity-0 cursor-pointer disabled:cursor-not-allowed"
            onChange={handleFileSelect}
          />
        </div>
        {clearable && previewUrl && (
          <ActionButton
            icon={IconTrash}
            variant="outline"
            color="red.5"
            disabled={disabled}
            aria-label={t("common.imagePicker.remove")}
            onClick={handleClear}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={clsx("h-full flex flex-col gap-2 text-gray-400", isBox ? "w-fit" : "w-full")}>
      <ImageLightbox
        src={previewUrl}
        alt={alt ?? label}
        opened={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
      <span className="text-xs font-bold text-slate-600">{label}</span>
      {isBox ? (
        <div className="flex-1 min-h-0 flex flex-col items-stretch gap-3">
          {preview}
          {controls}
        </div>
      ) : (
        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4">
          {preview}
          {controls}
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
